import { createAsyncThunk,createSlice} from "@reduxjs/toolkit";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface ApplicationErrorRecord {
  emplApplErrId: string;
  [key: string]: any;
}
export interface AdministerState {
  uploadStatus: string;
  uploadError: string | null;
  uploadedFileName: string | null;
  errorRecords: ApplicationErrorRecord[];
  errorRecordsStatus: AsyncStatus;
  errorRecordsError: string | null;
  deleteStatus: AsyncStatus;
  deleteError: string | null;
  deleteAllStatus?: AsyncStatus;
  deleteAllError?: string | null;
  submitCorrectedStatus?: AsyncStatus;
  submitCorrectedError?: string | null;
}


const initialState: AdministerState = {
  uploadStatus: 'idle',
  uploadError: null,
  uploadedFileName: null,
  errorRecords: [],
  errorRecordsStatus: 'idle',
  errorRecordsError: null,
  deleteStatus: 'idle',
  deleteError: null,
  deleteAllStatus: 'idle',
  deleteAllError: null,
  submitCorrectedStatus: 'idle',
  submitCorrectedError: null,
};


export const uploadExcelFile = createAsyncThunk(
  "admin/uploadExcelFile",
  async (
    {

      file,

    }: {
      file: File;
      nuid?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("templateType", "Employee_Application"); 

      await api.post(
        endpoints.administerSystem.uploadExcel,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return {
        fileName: file.name,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload Excel file");
    }
  }
);

export const getApplicationErrors = createAsyncThunk(
  "admin/getApplicationErrors",
  async (
    { page, perPage }: { page: number; perPage: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(endpoints.administerSystem.getApplicationErrors(page, perPage));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch application errors");
    }
  }
);

export const deleteOneRecord = createAsyncThunk(
  "admin/deleteOneRecord",
  async (
    { emplApplErrId }: { emplApplErrId: string },
    { rejectWithValue }
  ) => {
    try {
      await api.delete(endpoints.administerSystem.deleteOneRecord(emplApplErrId));
      return emplApplErrId;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete error record");
    }
  }
);

export const deleteAllRecords = createAsyncThunk(
  "admin/deleteAllRecords",
  async (_, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.administerSystem.deleteAllRecords);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete all error records");
    }
  }
);

export const submitCorrectedErrors = createAsyncThunk(
  "admin/submitCorrectedErrors",
  async (
    { emplApplErrId, payload }: { emplApplErrId: string; payload: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        endpoints.administerSystem.submitCorrectedErrors(emplApplErrId),
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to submit corrected errors");
    }
  }
);


const administerSlice = createSlice({
  name: "administer",
  initialState,
  reducers: {
    clearUploadStatus(state) {
      state.uploadStatus = 'idle';
      state.uploadError = null;
      state.uploadedFileName = null;
    },
    clearErrorRecords(state) {
      state.errorRecords = [];
      state.errorRecordsStatus = 'idle';
      state.errorRecordsError = null;
    },
    clearDeleteStatus(state) {
      state.deleteStatus = 'idle';
      state.deleteError = null;
    },
    clearDeleteAllStatus(state) {
      state.deleteAllStatus = 'idle';
      state.deleteAllError = null;
    },
    clearSubmitCorrectedStatus(state) {
      state.submitCorrectedStatus = 'idle';
      state.submitCorrectedError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload Excel
      .addCase(uploadExcelFile.pending, (state) => {
        state.uploadStatus = 'loading';
        state.uploadError = null;
        state.uploadedFileName = null;
      })
      .addCase(uploadExcelFile.fulfilled, (state, action) => {
        state.uploadStatus = 'succeeded';
        state.uploadedFileName = action.payload.fileName;
      })
      .addCase(uploadExcelFile.rejected, (state, action) => {
        state.uploadStatus = 'failed';
        state.uploadError = action.payload as string || 'Upload failed';
      })

      // Get Application Errors
      .addCase(getApplicationErrors.pending, (state) => {
        state.errorRecordsStatus = 'loading';
        state.errorRecordsError = null;
      })
      .addCase(getApplicationErrors.fulfilled, (state, action) => {
        state.errorRecordsStatus = 'succeeded';
        state.errorRecords = action.payload.data || [];
      })
      .addCase(getApplicationErrors.rejected, (state, action) => {
        state.errorRecordsStatus = 'failed';
        state.errorRecordsError = action.payload as string || 'Failed to fetch application errors';
      })

      // Delete One Record
      .addCase(deleteOneRecord.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteOneRecord.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.errorRecords = state.errorRecords.filter(
          (rec) => rec.emplApplErrId !== action.payload
        );
      })
      .addCase(deleteOneRecord.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload as string || 'Failed to delete error record';
      })

      // Delete All Records
      .addCase(deleteAllRecords.pending, (state) => {
        state.deleteAllStatus = 'loading';
        state.deleteAllError = null;
      })
      .addCase(deleteAllRecords.fulfilled, (state) => {
        state.deleteAllStatus = 'succeeded';
        state.errorRecords = [];
      })
      .addCase(deleteAllRecords.rejected, (state, action) => {
        state.deleteAllStatus = 'failed';
        state.deleteAllError = action.payload as string || 'Failed to delete all error records';
      })

      // Submit Corrected Errors
      .addCase(submitCorrectedErrors.pending, (state) => {
        state.submitCorrectedStatus = 'loading';
        state.submitCorrectedError = null;
      })
      .addCase(submitCorrectedErrors.fulfilled, (state) => {
        state.submitCorrectedStatus = 'succeeded';
      })
      .addCase(submitCorrectedErrors.rejected, (state, action) => {
        state.submitCorrectedStatus = 'failed';
        state.submitCorrectedError = action.payload as string || 'Failed to submit corrected errors';
      });
  },
});

export const { clearUploadStatus, clearErrorRecords, clearDeleteStatus, clearDeleteAllStatus, clearSubmitCorrectedStatus } = administerSlice.actions;

export default administerSlice.reducer;